
import { Component, Vue } from "vue-property-decorator";

import { User } from "@/store/user/types";
import { Activity, ActualActivity, ContentAssignment, ContentItem, Phase, Program, StateType, GenericState } from "@/types";
import { ROOT_ACTIONS } from "@/store/actions";
import { nl, fr } from "date-fns/locale";
import { format } from "date-fns";
import { USER_GETTERS } from "@/store/user/getters";
import { addDoc, collection, collectionGroup, doc, documentId, getDoc, getDocs, orderBy, query, Timestamp, updateDoc, where } from "firebase/firestore";
import { firestore } from "@/firebase";
import { pickCurrentProgram } from "@/utils/programAccess";

@Component({
  components: {
    ProgramTypeSpan: () =>
      import("@/components/typography/ProgramTypeSpan.vue"),
    ProgramFormulaSpan: () => import('@/components/typography/ProgramFormulaSpan.vue'),
    AcitivityTypeSpan: () =>
      import("@/components/typography/ActivityTypeSpan.vue"),
  },
})
export default class Home extends Vue {
  get user(): User {
    return this.$store.getters[USER_GETTERS.ACTING_USER]
  }

  program: Program | null = null;
  phases: Phase[] = [];
  states: GenericState[] = []
  activities = [] as Activity[];
  actualActivities = [] as ActualActivity[];

  coach: User | null = null;
  contentAssignments = [] as ContentAssignment[];
  contentItems = {} as Record<string, ContentItem>;
  webinars = [] as any[];
  allWebinars = [] as any[];
  registeringWebinarId: string | null = null;

  loading = true;
  loadingContent = true;
  loadingWebinars = true;

  noProgram = false;
  currentStep = 1;

  formatDate(date: string | Date) {
    return format(new Date(date), 'EEEE dd/MM/yyyy - HH:mm', {
      locale: this.user.language == 'fr' ? fr : nl,
    })
  }

  get activitiesToConfirm(){
    // if Screencast or Webinar is in the name, it should not be confirmed (case insensitive)
    return this.actualActivities.filter(e => !e.participantConfirmed && !/screencast|webinar/i.test(this.getActivityName(e.activityId)))
  }

  get validContentAssignments() {
    return this.contentAssignments.filter(assignment => 
      assignment.contentId && this.contentItems[assignment.contentId] &&
      assignment.requestedBy !== this.user.id
    );
  }

  formatHours(hours: number) {
    let rhours = Math.floor(hours);
    let minutes = hours % 1;
    if (minutes === 0) return `${rhours}h`;

    let rminutes = Math.ceil(minutes * 60)

    return `${rhours}h${rminutes}m`
  }

  loadingConfirmation = '';


  getActualHoursForActivity(activity: Activity) {
    const hours = this.actualActivities?.filter(e => e.activityId === activity.id).map(e => e.actualHours).reduce((a, b) => a! + b!, 0);
    if (isNaN(hours as number)) return 0;
    return hours?.toFixed(0);
  }

  get isSuspended() {
    return this.program?.state === StateType.SUSPENDED
  }

  async mounted() {
    this.$store.dispatch(ROOT_ACTIONS.SET_BREADCRUMBS, [])

    await this.fetchProgram();
    await this.fetchContentAssignments();
    await this.fetchWebinars();
    await this.fetchAllUpcomingWebinars();

    if (this.program) {
      const coach = await getDoc(doc(firestore, 'users', this.program.coach))
      this.coach = {
        ...coach.data(),
        id: coach.id
      } as User
    }
    this.loading = false;
  }

  async fetchProgram() {
    console.log(this.user.id)
    const programs = await getDocs(query(collection(firestore, 'programs'), where('participant', '==', this.user.id)))
    if (programs.size == 0) {
      this.noProgram = true;
      return;
    }

    this.phases = []
    this.activities = []
    this.actualActivities = []

    try {
      const currentProgram = pickCurrentProgram(programs.docs.map(e => ({ ...e.data(), id: e.id } as Program)))!
      const fetchedProgram = await getDoc(doc(firestore, 'programs', currentProgram.id))
      const fetchedPhases = await getDocs(query(collection(firestore, 'phases'), where('programId', '==', fetchedProgram.id), orderBy('index', 'asc')))

      const fetchedStates = await getDocs(query(collection(firestore, 'program-states'), where('programId', '==', fetchedProgram.id), orderBy('date', 'asc')))


      for (let phase of fetchedPhases.docs) {
        this.phases.push({
          ...phase.data(),
          id: phase.id,
        } as Phase);
        const fetchedActivities = await getDocs(query(collection(firestore, 'activities'), where('phaseId', '==', phase.id)))
        for (let activity of fetchedActivities.docs) {
          const fetchedActualActivities = await getDocs(query(collection(firestore, 'actual-activities'), where('activityId', '==', activity.id)))
          this.activities.push({
            ...activity.data(),
            id: activity.id
          } as Activity)
          for (let actualActivity of fetchedActualActivities.docs) {
            this.actualActivities.push({
              ...actualActivity.data(),
              id: actualActivity.id
            } as ActualActivity)
          }
        }
      }



      this.program = {
        ...fetchedProgram.data(),
        id: fetchedProgram.id,
      } as Program

      console.log(this.actualActivities)


      this.states = fetchedStates.docs.map(e => {
        return {
          ...e.data(),
          id: e.id
        } as GenericState
      })


      if (this.program) {
        const coach = await getDoc(doc(firestore, 'users', this.program.coach))

        this.coach = {
          ...coach.data(),
          id: coach.id
        } as User
      }
    } catch (error) {
      console.log(error)
    }
  }

  async confirmActivity(actualActivityId: string | undefined) {
    if (!actualActivityId) return;
    this.loadingConfirmation = actualActivityId;
    await updateDoc(doc(firestore, 'actual-activities', actualActivityId), {
      participantConfirmed: true
    })

    // await this.fetchProgram();
    this.actualActivities.find(e => e.id === actualActivityId)!.participantConfirmed = true;

    this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
      color: "success",
      text: this.$t('activityConfirmed'),
    });

    this.loadingConfirmation = '';
    location.reload();
  }

  get sortedUpcomingWebinars() {
    const now = new Date();
    return this.webinars
      .filter(w => {
        if (!w.start) return false;
        const webinarDate = w.start.toDate ? w.start.toDate() : new Date(w.start);
        return webinarDate >= now;
      })
      .sort((a, b) => {
        const dateA = a.start.toDate ? a.start.toDate() : new Date(a.start);
        const dateB = b.start.toDate ? b.start.toDate() : new Date(b.start);
        return dateA.getTime() - dateB.getTime();
      });
  }

  get sortedPastWebinars() {
    const now = new Date();
    return this.webinars
      .filter(w => {
        if (!w.start) return false;
        const webinarDate = w.start.toDate ? w.start.toDate() : new Date(w.start);
        return webinarDate < now;
      })
      .sort((a, b) => {
        const dateA = a.start.toDate ? a.start.toDate() : new Date(a.start);
        const dateB = b.start.toDate ? b.start.toDate() : new Date(b.start);
        return dateB.getTime() - dateA.getTime(); // Most recent first
      });
  }

  get sortedRegisteredWebinars() {
    // Keep for backward compatibility, but not used in template anymore
    return [...this.sortedUpcomingWebinars, ...this.sortedPastWebinars];
  }

  get allUpcomingWebinars() {
    const now = new Date();
    const upcoming = this.allWebinars.filter(w => {
      if (!w.start) return false;
      const webinarDate = w.start.toDate ? w.start.toDate() : new Date(w.start);
      return webinarDate >= now;
    }).sort((a, b) => {
      const dateA = a.start.toDate ? a.start.toDate() : new Date(a.start);
      const dateB = b.start.toDate ? b.start.toDate() : new Date(b.start);
      return dateA.getTime() - dateB.getTime();
    });
    return upcoming;
  }

  isRegisteredForWebinar(webinarId: string) {
    return this.webinars.some(w => w.id === webinarId);
  }

  async registerForWebinar(webinar: any) {
    this.registeringWebinarId = webinar.id;
    try {
      let coach = this.program?.coach || '';
      if (!coach) {
        const programs = await getDocs(
          query(collection(firestore, 'programs'), where('participant', '==', this.user.id))
        );
        if (!programs.empty) {
          coach = programs.docs[0].data().coach as string;
        }
      }

      await addDoc(collection(firestore, 'event-attendees'), {
        user: this.user.id,
        webinarId: webinar.id,
        coach,
        present: false,
        addedActivity: false,
        registeredByCoach: false,
      });

      await addDoc(collection(firestore, 'webinar-invite-test'), {
        user: this.user.id,
        webinar: webinar.id,
      });
      this.webinars.push(webinar);
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'success',
        text: this.$t('registeredForWebinar'),
      });
    } catch (error) {
      console.error('Error registering for webinar:', error);
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: 'Error registering for webinar',
      });
    } finally {
      this.registeringWebinarId = null;
    }
  }

  isWebinarUpcoming(webinar: any) {
    if (!webinar.start) return false;
    const webinarDate = webinar.start.toDate ? webinar.start.toDate() : new Date(webinar.start);
    return webinarDate >= new Date();
  }

  isWebinarRecentlyPast(webinar: any) {
    if (!webinar.start) return false;
    const webinarDate = webinar.start.toDate ? webinar.start.toDate() : new Date(webinar.start);
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
    return webinarDate >= oneDayAgo && webinarDate < now;
  }

  openTeamsLink(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  async fetchWebinars() {
    try {
      this.loadingWebinars = true;
      const attendees = await getDocs(query(collection(firestore, 'event-attendees'), where('user', '==', this.user.id)))
      
      for (let attendee of attendees.docs) {
        const attendeeData = attendee.data();
        
        if (!attendeeData.webinarId) {
          continue;
        }
        
        const webinarData = await getDoc(doc(firestore, 'webinars', attendeeData.webinarId))
        if (webinarData.exists()) {
          this.webinars.push({
            id: webinarData.id,
            ...webinarData.data(),
          });
        }
      }
    } catch (error) {
      console.error('Error fetching registered webinars:', error);
    } finally {
      this.loadingWebinars = false;
    }
  }

  async fetchAllUpcomingWebinars() {
    try {
      const now = Timestamp.fromDate(new Date());
      const webinarDocs = await getDocs(query(collection(firestore, 'webinars'), where('start', '>=', now), orderBy('start', 'asc')));
      
      // Filter webinars by language - show webinars where language is unset OR matches user's language
      const userLanguage = this.user.language === 'fr' ? 'fr' : 'nl';
      
      this.allWebinars = webinarDocs.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        } as any))
        .filter((webinar: any) => !webinar.language || webinar.language === userLanguage);
    } catch (error) {
      console.error('Error fetching all webinars:', error);
    }
  }

  async fetchContentAssignments() {
    try {
      this.loadingContent = true;
      const contentAssignments = await getDocs(query(collection(firestore, 'content-assignments'), where('user', '==', this.user.id)))
      if (contentAssignments.size == 0) {
        this.loadingContent = false;
        return;
      }

      // Filter out assignments without contentId
      const validContentIds = contentAssignments.docs
        .map(e => e.data().contentId)
        .filter(id => id != null && id !== '');
      
      if (validContentIds.length === 0) {
        console.warn('No valid content IDs found in assignments');
        this.loadingContent = false;
        return;
      }

      const contentItems = await getDocs(query(collection(firestore, 'content-items'), where(documentId(), 'in', validContentIds)))

      for (let contentItem of contentItems.docs) {
        this.contentItems[contentItem.id] = {
          ...contentItem.data(),
          id: contentItem.id
        } as ContentItem
      }

      this.contentAssignments = contentAssignments.docs.map(e => {
        return {
          id: e.id,
          ...e.data(),
        } as ContentAssignment
      })
      
      console.log('Loaded content assignments:', this.contentAssignments.length);
      console.log('Loaded content items:', Object.keys(this.contentItems).length);
    } catch (error) {
      console.error('Error fetching content assignments:', error)
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: 'Error loading screencasts'
      })
    } finally {
      this.loadingContent = false;
    }
  }

  getActivityName(activityId: string) {
    return this.activities.find(e => e.id === activityId)?.name || ''
  }
}
